import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import {
  InterviewTemplateActions,
  InterviewTemplateScope,
  InterviewTemplateSelectors,
} from '@core/interview-templates-data';
import {
  InterviewTemplateKind,
  CandidateInterviewTemplates,
  type InterviewTemplate,
} from '@core/interview-templates-types';
import { SelectGroupedOptions, SelectGroupOption } from '@core/user-interface-legacy';

export interface GroupedCandidateTemplates {
  groups: SelectGroupedOptions[];
  preselectedTemplateId: number | null;
}

export interface CandidateTemplateItemOption {
  id: number;
  name: string;
  questionsCount: number;
  hasSkillsEvaluation: boolean;
}

export interface CandidateTemplateItemGroup {
  title: string;
  options: CandidateTemplateItemOption[];
}

@Injectable({ providedIn: 'root' })
export class CandidateInterviewTemplatesService {
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);

  public areCandidateTemplatesLoaded({
    candidateId,
    offerId,
    kind,
  }: {
    candidateId: number;
    offerId: number;
    kind: InterviewTemplateKind;
  }): Observable<boolean> {
    const baseScope = `${candidateId}-${offerId}-${kind}`;

    return combineLatest([
      this.store.select(InterviewTemplateSelectors.isLoaded({ scope: `${baseScope}-stage` })),
      this.store.select(InterviewTemplateSelectors.isLoaded({ scope: `${baseScope}-offer` })),
      this.store.select(InterviewTemplateSelectors.isLoaded({ scope: `${baseScope}-other` })),
    ]).pipe(map(states => states.every(Boolean)));
  }

  public fetchGroupedCandidateTemplates(params: {
    candidateId: number;
    offerId: number;
    kind: InterviewTemplateKind;
  }): Observable<GroupedCandidateTemplates> {
    this.dispatchCandidateCollection(params);
    return this.getGroupedCandidateTemplates(params);
  }

  public getGroupedCandidateTemplates(params: {
    candidateId: number;
    offerId: number;
    kind: InterviewTemplateKind;
  }): Observable<GroupedCandidateTemplates> {
    return this.selectCandidateTemplates(params).pipe(
      map(candidateTemplates => this.mapToSelectOptions(candidateTemplates)),
    );
  }

  public fetchGroupedCandidateTemplateItems(params: {
    candidateId: number;
    offerId: number;
    kind: InterviewTemplateKind;
  }): Observable<CandidateTemplateItemGroup[]> {
    this.dispatchCandidateCollection(params);
    return this.selectCandidateTemplates(params).pipe(
      map(candidateTemplates => this.mapToItemGroups(candidateTemplates)),
    );
  }

  public getGlobalTemplateItems(): Observable<CandidateTemplateItemGroup[]> {
    return this.store
      .select(InterviewTemplateSelectors.entities({ scope: InterviewTemplateScope.Interview }))
      .pipe(map(templates => this.groupTemplatesByCategory(templates ?? [])));
  }

  public areGlobalTemplatesLoaded(): Observable<boolean> {
    return this.store.select(
      InterviewTemplateSelectors.isLoaded({ scope: InterviewTemplateScope.Interview }),
    );
  }

  private dispatchCandidateCollection({
    candidateId,
    offerId,
  }: {
    candidateId: number;
    offerId: number;
  }): void {
    this.store.dispatch(
      InterviewTemplateActions.fetchCandidateCollection({
        payload: { candidateId, offerId, kind: 'interview' },
      }),
    );
  }

  private selectCandidateTemplates({
    candidateId,
    offerId,
    kind,
  }: {
    candidateId: number;
    offerId: number;
    kind: InterviewTemplateKind;
  }): Observable<CandidateInterviewTemplates> {
    const baseScope = `${candidateId}-${offerId}-${kind}`;

    return combineLatest({
      stageTemplates: this.store.select(
        InterviewTemplateSelectors.entities({ scope: `${baseScope}-stage` }),
      ),
      offerTemplates: this.store.select(
        InterviewTemplateSelectors.entities({ scope: `${baseScope}-offer` }),
      ),
      otherTemplates: this.store.select(
        InterviewTemplateSelectors.entities({ scope: `${baseScope}-other` }),
      ),
    });
  }

  private mapToSelectOptions(
    candidateTemplates: CandidateInterviewTemplates,
  ): GroupedCandidateTemplates {
    const { stageTemplates, offerTemplates, otherTemplates } = candidateTemplates;
    const groups = [
      ...this.mapTemplates(otherTemplates),
      {
        title: this.i18n.translate('app.interview.templates.offer'),
        options: [
          ...stageTemplates.map(t => ({
            label: t.name,
            value: t.id,
            badgeText: this.i18n.translate('app.interview.templates.stage'),
          })),
          ...offerTemplates.map(t => ({
            label: t.name,
            value: t.id,
          })),
        ],
      },
    ].filter(group => group.options.length > 0);

    return {
      groups,
      preselectedTemplateId: stageTemplates[0]?.id ?? null,
    };
  }

  private mapToItemGroups(
    candidateTemplates: CandidateInterviewTemplates,
  ): CandidateTemplateItemGroup[] {
    const { offerTemplates, otherTemplates } = candidateTemplates;

    const kitGroup: CandidateTemplateItemGroup = {
      title: this.i18n.translate('app.interview.templates.offer'),
      options: offerTemplates.map(template => this.toItemOption(template)),
    };

    return [kitGroup, ...this.groupTemplatesByCategory(otherTemplates)].filter(
      group => group.options.length > 0,
    );
  }

  private groupTemplatesByCategory(templates: InterviewTemplate[]): CandidateTemplateItemGroup[] {
    const otherLabel = this.i18n.translate('app.common.other');

    return Object.entries(
      templates.reduce<Record<string, CandidateTemplateItemOption[]>>((acc, template) => {
        const category = template.category || otherLabel;
        (acc[category] ??= []).push(this.toItemOption(template));
        return acc;
      }, {}),
    )
      .map(([title, options]) => ({ title, options }))
      .sort((a, b) => {
        if (a.title === otherLabel) return -1;
        if (b.title === otherLabel) return 1;
        return a.title.localeCompare(b.title);
      })
      .reverse()
      .filter(group => group.options.length > 0);
  }

  private toItemOption(template: InterviewTemplate): CandidateTemplateItemOption {
    return {
      id: template.id,
      name: template.name,
      questionsCount: template.questionsCount,
      hasSkillsEvaluation: template.hasSkillsEvaluation,
    };
  }

  private mapTemplates(templates: InterviewTemplate[]): SelectGroupedOptions[] {
    const otherLabel = this.i18n.translate('app.common.other');
    const groups = templates.reduce<Record<string, SelectGroupOption[]>>((acc, template) => {
      const category = template.category || otherLabel;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({ label: template.name, value: template.id! });
      return acc;
    }, {});

    return Object.entries(groups)
      .map(([title, options]) => ({ title, options }))
      .sort((a, b) => {
        if (a.title === otherLabel) return -1;
        if (b.title === otherLabel) return 1;
        return a.title.localeCompare(b.title);
      })
      .reverse();
  }
}
